import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { photosApi, ApiError } from "@/api";
import type { DrawingSet, PhotoSet, PhotoUploadFileDescriptor, PhotoUploadTicket, PhotosResponse, SitePhoto } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { formatDateShort } from "@/lib/format";
import { formatBytes } from "@/lib/shares";
import {
  PHOTO_ACCEPT,
  PHOTO_BATCH_SIZE,
  PHOTO_MAX_BYTES,
  fileSlug,
  formatDayHeading,
  isAcceptedPhoto,
  localDayKey,
  mapWithConcurrency,
  photoFileName,
  prepareThumbnail,
  putWithProgress,
  readExifTakenAt,
  safeFileName,
  saveBlob,
} from "@/lib/photos";
import { ZIP_MAX_BYTES, buildZip, crc32, uniquePath, type ZipEntry } from "@/lib/zip";
import { PhotoLightbox } from "./PhotoLightbox";
import { PhotoSetDrawer } from "./PhotoSetDrawer";
import "./photos.css";

interface Props {
  jobId: number;
  jobNumber: number;
  /** Production drawing sets — the optional link a photo set can carry. */
  drawingSets: DrawingSet[];
  onError: (msg: string | undefined) => void;
  onNotice: (kind: "info" | "warn", message: string) => void;
  /** Reports the live photo count up so the tab label stays in sync. */
  onCount?: (n: number) => void;
}

type UploadStatus = "preparing" | "waiting" | "uploading" | "done" | "failed";

interface UploadItem {
  key: number;
  name: string;
  status: UploadStatus;
  progress: number;
  error?: string;
}

interface PreparedUpload {
  file: File;
  thumb: Blob;
  desc: PhotoUploadFileDescriptor;
}

interface DayGroup {
  key: string;
  label: string;
  photos: SitePhoto[];
}

interface Section {
  set: PhotoSet;
  days: DayGroup[];
  count: number;
}

let nextUploadKey = 1;

/** Past this a zip download asks first — it's a long wait on a phone. */
const ZIP_CONFIRM_BYTES = 500 * 1024 * 1024;

/** Job Detail › Photos. One read (GET staff/photos) gives the sets and every
 *  live photo with a 1 h thumbnail URL; the tab groups them by set (newest
 *  visit first) and by taken-day inside a set. Uploads go browser → R2 via
 *  presigned PUTs (see lib/photos.ts), with EXIF date-taken deciding the set
 *  when "auto by date" is chosen. Multi-select moves, downloads or deletes
 *  photos; the lightbox handles one photo at a time. Downloads fetch the
 *  originals the same way the thumbnail self-heal does and zip them in the
 *  browser (lib/zip.ts) — one photo saves as itself, more as a zip. */
export function PhotosTab({ jobId, jobNumber, drawingSets, onError, onNotice, onCount }: Props) {
  const [data, setData] = useState<PhotosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSetId, setSelectedSetId] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSetId, setUploadSetId] = useState(0);
  const [drawer, setDrawer] = useState<{ set: PhotoSet | null } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedAt = useRef(0);
  const refreshing = useRef(false);
  // Self-heal state for photos that arrived without a thumbnail (MCP uploads
  // that skipped it, URL imports): one attempt per photo per mount, one at a
  // time, never while an upload is running. Refs, not state, drive the loop so
  // a re-render can't cancel it mid-photo; `healing` is display only.
  const healAttempted = useRef<Set<number>>(new Set());
  const healRunning = useRef(false);
  const mounted = useRef(true);
  const [healing, setHealing] = useState<{ done: number; total: number } | null>(null);
  const [zipping, setZipping] = useState<{ done: number; total: number } | null>(null);

  const fail = (err: unknown, fallback: string) => onError(err instanceof ApiError ? err.message : fallback);

  /** The original's bytes, via a fresh 300 s presigned URL (the bucket's CORS
   *  allows GET from the app). Throws on either step. */
  async function fetchOriginal(p: SitePhoto): Promise<Blob> {
    const { url } = await photosApi.getPhotoDownloadUrl(p.id);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Original fetch failed (${res.status}).`);
    return res.blob();
  }

  /** Make and store the thumbnail for one photo that has none: fetch the
   *  original, draw the 512px JPEG with the uploader's own helper, PUT it to a
   *  presigned thumbnail slot, confirm. Throws on any step; the caller leaves
   *  the original in place. */
  async function makeThumbnailFor(p: SitePhoto) {
    const blob = await fetchOriginal(p);
    const file = new File([blob], p.file_name || "photo.jpg", { type: p.mime_type || blob.type || "image/jpeg" });
    const { thumb } = await prepareThumbnail(file);
    const { url: putUrl } = await photosApi.getThumbUploadUrl(p.id);
    await putWithProgress(putUrl, thumb, "image/jpeg");
    await photosApi.confirmThumb(p.id);
  }

  /** Run the self-heal over whatever the latest list says lacks a thumbnail. */
  async function healMissingThumbs(list: SitePhoto[]) {
    if (healRunning.current) return;
    const missing = list.filter((p) => !p.has_thumb && !healAttempted.current.has(p.id));
    if (missing.length === 0) return;
    healRunning.current = true;
    setHealing({ done: 0, total: missing.length });
    let done = 0;
    try {
      for (const p of missing) {
        if (!mounted.current) break;
        healAttempted.current.add(p.id);
        try {
          await makeThumbnailFor(p);
          if (mounted.current) {
            setData((d) => (d ? { ...d, photos: d.photos.map((x) => (x.id === p.id ? { ...x, has_thumb: true } : x)) } : d));
          }
        } catch {
          /* leave the original as the tile; retried on the next mount */
        }
        done += 1;
        if (mounted.current) setHealing({ done, total: missing.length });
      }
    } finally {
      healRunning.current = false;
      if (mounted.current) setHealing(null);
    }
  }

  const load = useCallback(
    async (opts: { initial?: boolean } = {}) => {
      if (opts.initial) setLoading(true);
      try {
        const res = await photosApi.listPhotos(jobId);
        setData(res);
        loadedAt.current = Date.now();
        onCount?.(res.photos.length);
        void healMissingThumbs(res.photos);
      } catch (err) {
        fail(err, "Could not load the photos.");
      } finally {
        if (opts.initial) setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jobId],
  );

  useEffect(() => {
    mounted.current = true;
    void load({ initial: true });
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const sets = useMemo(() => data?.sets ?? [], [data]);
  const photos = useMemo(() => data?.photos ?? [], [data]);

  // A deleted / vanished set can't stay selected.
  useEffect(() => {
    if (selectedSetId && !sets.some((s) => s.id === selectedSetId)) setSelectedSetId(0);
  }, [sets, selectedSetId]);

  // The upload target follows the selected chip (All → auto by date).
  useEffect(() => {
    setUploadSetId(selectedSetId);
  }, [selectedSetId]);

  // Clear a fully-successful upload queue after a beat; failures stay visible.
  useEffect(() => {
    if (uploading || queue.length === 0) return;
    if (!queue.every((q) => q.status === "done")) return;
    const t = window.setTimeout(() => setQueue([]), 3000);
    return () => window.clearTimeout(t);
  }, [queue, uploading]);

  const selectedSet = sets.find((s) => s.id === selectedSetId) ?? null;
  const visible = useMemo(
    () => (selectedSetId ? photos.filter((p) => p.photo_set_id === selectedSetId) : photos),
    [photos, selectedSetId],
  );

  const sections = useMemo<Section[]>(() => {
    const bySet = new Map<number, SitePhoto[]>();
    for (const p of visible) {
      const list = bySet.get(p.photo_set_id);
      if (list) list.push(p);
      else bySet.set(p.photo_set_id, [p]);
    }
    const out: Section[] = [];
    for (const set of sets) {
      const list = bySet.get(set.id);
      if (!list) continue;
      const days: DayGroup[] = [];
      for (const p of list) {
        const key = localDayKey(p.taken_at);
        const last = days[days.length - 1];
        if (last && last.key === key) last.photos.push(p);
        else days.push({ key, label: formatDayHeading(p.taken_at), photos: [p] });
      }
      out.push({ set, days, count: list.length });
    }
    return out;
  }, [sets, visible]);

  // The lightbox walks the photos in the order they're drawn.
  const flat = useMemo(() => sections.flatMap((s) => s.days.flatMap((d) => d.photos)), [sections]);
  const flatIndex = useMemo(() => new Map(flat.map((p, i) => [p.id, i])), [flat]);

  /** A thumbnail URL past its hour → one quiet re-read of the list. */
  function onThumbError() {
    if (refreshing.current || Date.now() - loadedAt.current < 60_000) return;
    refreshing.current = true;
    void load().finally(() => {
      refreshing.current = false;
    });
  }

  /* ── upload ─────────────────────────────────────────────────────────── */

  function updateItem(key: number, patch: Partial<UploadItem>) {
    setQueue((q) => q.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  async function handleFiles(list: FileList | File[]) {
    if (uploading) return;
    const all = Array.from(list);
    const accepted = all.filter((f) => isAcceptedPhoto(f) && f.size <= PHOTO_MAX_BYTES);
    const skipped = all.length - accepted.length;
    if (skipped > 0) onNotice("warn", `${skipped} file${skipped === 1 ? "" : "s"} skipped — JPEG, PNG or WebP under 30 MB only.`);
    if (accepted.length === 0) return;

    const items: UploadItem[] = accepted.map((f) => ({ key: nextUploadKey++, name: f.name, status: "preparing", progress: 0 }));
    setQueue(items);
    setUploading(true);
    onError(undefined);
    const targetSetId = uploadSetId;
    let added = 0;
    try {
      // 1. EXIF + thumbnail, two at a time — decoding 12 MP JPEGs is memory-heavy on a phone.
      const prepared = await mapWithConcurrency(accepted, 2, async (file, i): Promise<PreparedUpload | null> => {
        const key = items[i].key;
        try {
          const [exif, thumb] = await Promise.all([readExifTakenAt(file), prepareThumbnail(file)]);
          const takenAt = exif ?? (file.lastModified > 0 ? file.lastModified : Date.now());
          updateItem(key, { status: "waiting" });
          return {
            file,
            thumb: thumb.thumb,
            desc: {
              file_name: file.name,
              mime_type: file.type,
              file_size: file.size,
              width: thumb.width,
              height: thumb.height,
              taken_at: takenAt,
              taken_day: localDayKey(takenAt),
            },
          };
        } catch (err) {
          updateItem(key, { status: "failed", error: err instanceof Error ? err.message : "Could not read the image." });
          return null;
        }
      });
      const ready = prepared
        .map((p, i) => ({ p, key: items[i].key }))
        .filter((x): x is { p: PreparedUpload; key: number } => x.p != null);

      // 2. Batches of 25: init → PUT original + thumbnail (three in flight) → confirm.
      for (let b = 0; b < ready.length; b += PHOTO_BATCH_SIZE) {
        const batch = ready.slice(b, b + PHOTO_BATCH_SIZE);
        let tickets: PhotoUploadTicket[];
        try {
          tickets = (await photosApi.initPhotoUpload(jobId, batch.map((x) => x.p.desc), targetSetId || undefined)).uploads;
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : "Could not start the upload.";
          batch.forEach((x) => updateItem(x.key, { status: "failed", error: msg }));
          continue;
        }
        const results = await mapWithConcurrency(batch, 3, async (x, k) => {
          const t = tickets[k];
          if (!t) {
            updateItem(x.key, { status: "failed", error: "No upload slot returned." });
            return null;
          }
          updateItem(x.key, { status: "uploading", progress: 0 });
          try {
            const etag = await putWithProgress(t.put_url, x.p.file, x.p.file.type, (f) => updateItem(x.key, { progress: f * 0.9 }));
            await putWithProgress(t.thumb_put_url, x.p.thumb, "image/jpeg");
            updateItem(x.key, { progress: 1 });
            return { id: t.id, etag, key: x.key };
          } catch (err) {
            updateItem(x.key, { status: "failed", error: err instanceof Error ? err.message : "Upload failed." });
            // Tidy the pending row (and whatever landed) — best effort.
            void photosApi.deletePhoto(t.id).catch(() => undefined);
            return null;
          }
        });
        const ok = results.filter((r): r is { id: number; etag: string; key: number } => r != null);
        if (ok.length === 0) continue;
        try {
          const { confirmed } = await photosApi.confirmPhotoUpload(ok.map(({ id, etag }) => ({ id, etag })));
          added += confirmed;
          ok.forEach((r) => updateItem(r.key, { status: "done" }));
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : "Uploaded but not confirmed — please try again.";
          ok.forEach((r) => updateItem(r.key, { status: "failed", error: msg }));
        }
      }
    } finally {
      setUploading(false);
    }
    await load();
    if (added > 0) onNotice("info", `${added} photo${added === 1 ? "" : "s"} added.`);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) void handleFiles(files);
    e.target.value = "";
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    if (!uploading && !dragOver) setDragOver(true);
  }
  function onDragLeave(e: DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragOver(false);
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) void handleFiles(files);
  }

  /* ── selection ──────────────────────────────────────────────────────── */

  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function exitSelect() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }

  async function moveSelected(setId: number) {
    if (bulkBusy || selectedIds.size === 0 || setId <= 0) return;
    setBulkBusy(true);
    try {
      const res = await photosApi.movePhotos(Array.from(selectedIds), setId);
      onNotice("info", `${res.moved} photo${res.moved === 1 ? "" : "s"} moved.`);
      exitSelect();
      await load();
    } catch (err) {
      fail(err, "Could not move the photos.");
    } finally {
      setBulkBusy(false);
    }
  }

  async function deleteSelected() {
    const n = selectedIds.size;
    if (bulkBusy || n === 0) return;
    if (!window.confirm(`Delete ${n} photo${n === 1 ? "" : "s"}? This can’t be undone.`)) return;
    setBulkBusy(true);
    try {
      const results = await mapWithConcurrency(Array.from(selectedIds), 3, (id) =>
        photosApi
          .deletePhoto(id)
          .then(() => true)
          .catch(() => false),
      );
      const done = results.filter(Boolean).length;
      if (done < n) onNotice("warn", `${n - done} photo${n - done === 1 ? "" : "s"} couldn’t be deleted.`);
      else onNotice("info", `${done} photo${done === 1 ? "" : "s"} deleted.`);
      exitSelect();
      await load();
    } finally {
      setBulkBusy(false);
    }
  }

  /* ── download ───────────────────────────────────────────────────────── */

  /** One photo saves as its original file. Throws; the caller reports. */
  async function downloadOne(photo: SitePhoto) {
    const blob = await fetchOriginal(photo);
    saveBlob(blob, photoFileName(photo));
  }

  /** Save `list` (in display order): one photo as itself, more as a zip named
   *  `zipName`. Originals come down three at a time and each is CRC'd as it
   *  lands; a photo that fails is left out and counted in the notice. A
   *  download spanning several sets gets a folder per set. */
  async function downloadPhotos(list: SitePhoto[], zipName: string) {
    if (zipping || list.length === 0) return;
    onError(undefined);
    if (list.length === 1) {
      setZipping({ done: 0, total: 1 });
      try {
        await downloadOne(list[0]);
      } catch (err) {
        fail(err, "Could not download the photo.");
      } finally {
        if (mounted.current) setZipping(null);
      }
      return;
    }

    const bytes = list.reduce((n, p) => n + (p.file_size || 0), 0);
    if (bytes > ZIP_MAX_BYTES) {
      onNotice("warn", `That’s ${formatBytes(bytes)} — too much for one zip. Download a set at a time.`);
      return;
    }
    if (bytes > ZIP_CONFIRM_BYTES && !window.confirm(`Download ${list.length} photos (${formatBytes(bytes)}) as a zip?`)) return;

    // Names are settled up front, in display order, so duplicates number
    // predictably (IMG_0001.jpg, IMG_0001 (2).jpg…).
    const labels = new Map(sets.map((s) => [s.id, s.label]));
    const byFolder = new Set(list.map((p) => p.photo_set_id)).size > 1;
    const used = new Set<string>();
    const names = list.map((p) =>
      uniquePath((byFolder ? `${safeFileName(labels.get(p.photo_set_id) ?? "") || "Photos"}/` : "") + photoFileName(p), used),
    );

    setZipping({ done: 0, total: list.length });
    let done = 0;
    try {
      const entries = await mapWithConcurrency(list, 3, async (p, i): Promise<ZipEntry | null> => {
        try {
          if (!mounted.current) return null;
          const data = await fetchOriginal(p);
          return { name: names[i], data, crc: await crc32(data), modified: p.taken_at };
        } catch {
          return null;
        } finally {
          done += 1;
          if (mounted.current) setZipping({ done, total: list.length });
        }
      });
      if (!mounted.current) return;
      const ok = entries.filter((e): e is ZipEntry => e != null);
      if (ok.length === 0) {
        onError("Could not download the photos.");
        return;
      }
      saveBlob(buildZip(ok), zipName);
      const missed = list.length - ok.length;
      if (missed > 0) onNotice("warn", `${missed} photo${missed === 1 ? "" : "s"} couldn’t be downloaded and ${missed === 1 ? "is" : "are"} missing from the zip.`);
    } catch (err) {
      fail(err, "Could not build the zip.");
    } finally {
      if (mounted.current) setZipping(null);
    }
  }

  /* ── lightbox + drawer callbacks ────────────────────────────────────── */

  async function captionPhoto(photo: SitePhoto, caption: string) {
    const trimmed = caption.trim();
    const row = await photosApi.updatePhoto(photo.id, trimmed ? { caption: trimmed } : { clear_caption: true });
    setData((d) => (d ? { ...d, photos: d.photos.map((p) => (p.id === photo.id ? { ...p, caption: row.caption } : p)) } : d));
  }
  async function movePhoto(photo: SitePhoto, setId: number) {
    await photosApi.updatePhoto(photo.id, { photo_set_id: setId });
    await load();
  }
  async function deletePhoto(photo: SitePhoto) {
    await photosApi.deletePhoto(photo.id);
    setLightbox(null);
    onNotice("info", "Photo deleted.");
    await load();
  }

  async function onSetSaved(saved: PhotoSet, wasNew: boolean) {
    setDrawer(null);
    onNotice("info", wasNew ? "Set created." : "Set saved.");
    await load();
    if (wasNew) setSelectedSetId(saved.id);
  }
  async function onSetDeleted() {
    setDrawer(null);
    setSelectedSetId(0);
    onNotice("info", "Set deleted.");
    await load();
  }

  /* ── render ─────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="photos">
        <div className="jobs-list__loading">
          <Spinner label="Loading photos…" />
        </div>
      </div>
    );
  }

  const linkedDrawingSet = selectedSet && selectedSet.drawing_set_id > 0 ? drawingSets.find((d) => d.id === selectedSet.drawing_set_id) ?? null : null;
  const showDayHeadings = (sec: Section) => selectedSetId !== 0 || sec.days.length > 1;
  const zipBase = `photos-${jobNumber}`;
  const downloadVisible = () =>
    void downloadPhotos(flat, selectedSet ? `${zipBase}-${fileSlug(selectedSet.label) || selectedSet.id}.zip` : `${zipBase}.zip`);
  const downloadSelected = () => void downloadPhotos(flat.filter((p) => selectedIds.has(p.id)), `${zipBase}-selected.zip`);

  return (
    <div className="photos">
      <div className="photos__head">
        <div className="photos__chips" role="tablist" aria-label="Photo sets">
          <button type="button" role="tab" aria-selected={selectedSetId === 0} className={`photos__chip${selectedSetId === 0 ? " photos__chip--active" : ""}`} onClick={() => setSelectedSetId(0)}>
            All <span className="photos__chip-count numeric">{photos.length}</span>
          </button>
          {sets.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={selectedSetId === s.id}
              className={`photos__chip${selectedSetId === s.id ? " photos__chip--active" : ""}`}
              onClick={() => setSelectedSetId(s.id)}
              title={formatDateShort(s.visit_date)}
            >
              {s.label} <span className="photos__chip-count numeric">{s.photo_count}</span>
            </button>
          ))}
          <button type="button" className="photos__chip photos__chip--new" onClick={() => setDrawer({ set: null })} disabled={uploading}>
            + New set
          </button>
        </div>
        <div className="photos__actions">
          {selectedSet ? (
            <Button variant="ghost" size="sm" onClick={() => setDrawer({ set: selectedSet })} disabled={uploading}>
              Edit set
            </Button>
          ) : null}
          {flat.length > 0 && !selectMode ? (
            <Button variant="ghost" size="sm" onClick={downloadVisible} loading={zipping != null}>
              {selectedSet ? "Download set" : "Download all"}
            </Button>
          ) : null}
          {photos.length > 0 ? (
            <Button variant={selectMode ? "secondary" : "ghost"} size="sm" onClick={selectMode ? exitSelect : () => setSelectMode(true)} disabled={uploading}>
              {selectMode ? "Done" : "Select"}
            </Button>
          ) : null}
          <label className="photos__target">
            <span>Add to</span>
            <select value={String(uploadSetId)} onChange={(e) => setUploadSetId(Number(e.target.value))} disabled={uploading} aria-label="Upload into">
              <option value="0">Auto by date taken</option>
              {sets.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" onClick={() => fileInputRef.current?.click()} loading={uploading}>
            Add photos
          </Button>
          <input ref={fileInputRef} type="file" accept={PHOTO_ACCEPT} multiple hidden onChange={onPick} />
        </div>
      </div>

      {healing ? (
        <p className="photos__set-meta" aria-live="polite">
          Making thumbnails for photos that arrived without one… <span className="numeric">{healing.done}/{healing.total}</span>
        </p>
      ) : null}

      {zipping && zipping.total > 1 ? (
        <p className="photos__set-meta" aria-live="polite">
          Preparing the download… <span className="numeric">{zipping.done}/{zipping.total}</span>
        </p>
      ) : null}

      {selectedSet ? (
        <p className="photos__set-meta">
          Visit {formatDateShort(selectedSet.visit_date)}
          {linkedDrawingSet ? (
            <>
              {" "}
              · Drawing set{" "}
              <span className="numeric">
                {jobNumber}-{linkedDrawingSet.set_number}
              </span>
              {linkedDrawingSet.label ? ` (${linkedDrawingSet.label})` : ""}
            </>
          ) : null}
        </p>
      ) : null}

      {selectMode ? (
        <div className="photos__bulk" role="toolbar" aria-label="Selected photos">
          <span className="photos__bulk-count">
            <strong className="numeric">{selectedIds.size}</strong> selected
          </span>
          <label className="photos__target">
            <span>Move to</span>
            <select value="" onChange={(e) => void moveSelected(Number(e.target.value))} disabled={bulkBusy || selectedIds.size === 0} aria-label="Move selected photos to set">
              <option value="">Choose a set…</option>
              {sets.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <Button variant="secondary" size="sm" onClick={downloadSelected} loading={zipping != null} disabled={bulkBusy || selectedIds.size === 0}>
            Download
          </Button>
          <Button variant="danger" size="sm" onClick={() => void deleteSelected()} loading={bulkBusy} disabled={zipping != null || selectedIds.size === 0}>
            Delete
          </Button>
        </div>
      ) : null}

      {queue.length > 0 ? (
        <ul className="photos__queue" aria-label="Uploads">
          {queue.map((q) => (
            <li key={q.key} className={`photos__queue-item photos__queue-item--${q.status}`}>
              <span className="photos__queue-name">{q.name}</span>
              <span className="photos__queue-state">
                {q.status === "preparing"
                  ? "Preparing…"
                  : q.status === "waiting"
                    ? "Waiting…"
                    : q.status === "uploading"
                      ? `${Math.round(q.progress * 100)}%`
                      : q.status === "done"
                        ? "Done"
                        : q.error ?? "Failed"}
              </span>
              <span className="photos__queue-bar" aria-hidden="true">
                <span style={{ width: `${Math.round((q.status === "done" ? 1 : q.progress) * 100)}%` }} />
              </span>
            </li>
          ))}
          {!uploading ? (
            <li className="photos__queue-clear">
              <Button variant="ghost" size="sm" onClick={() => setQueue([])}>
                Clear
              </Button>
            </li>
          ) : null}
        </ul>
      ) : null}

      <div className={`photos__drop${dragOver ? " photos__drop--over" : ""}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {sections.length === 0 ? (
          <p className="photos__empty">
            {selectedSet ? "No photos in this set yet. " : "No photos yet. "}
            Drop photos here or use <strong>Add photos</strong> — on a phone that opens the camera roll. Photos group into sets by the day they were taken.
          </p>
        ) : (
          sections.map((sec) => (
            <section key={sec.set.id} className="photos__section" aria-label={sec.set.label}>
              {selectedSetId === 0 ? (
                <h3 className="photos__section-title">
                  <button type="button" onClick={() => setSelectedSetId(sec.set.id)}>
                    {sec.set.label}
                  </button>
                  <span className="photos__section-meta">
                    {formatDateShort(sec.set.visit_date)} · <span className="numeric">{sec.count}</span>
                  </span>
                </h3>
              ) : null}
              {sec.days.map((day) => (
                <div key={day.key} className="photos__day">
                  {showDayHeadings(sec) ? (
                    <h4 className="photos__day-title">
                      {day.label} <span className="numeric">· {day.photos.length}</span>
                    </h4>
                  ) : null}
                  <div className="photo-grid">
                    {day.photos.map((p) => {
                      const selected = selectedIds.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className={`photo-tile${selected ? " photo-tile--selected" : ""}${selectMode ? " photo-tile--selectable" : ""}`}
                          onClick={() => (selectMode ? toggleSelected(p.id) : setLightbox(flatIndex.get(p.id) ?? 0))}
                          aria-pressed={selectMode ? selected : undefined}
                          title={p.caption || p.file_name}
                        >
                          <img src={p.thumb_url} alt={p.caption || p.file_name} loading="lazy" onError={onThumbError} />
                          {selectMode ? (
                            <span className="photo-tile__check" aria-hidden="true">
                              {selected ? "✓" : ""}
                            </span>
                          ) : null}
                          {p.caption ? <span className="photo-tile__caption">{p.caption}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))
        )}
      </div>

      {lightbox != null && flat[lightbox] ? (
        <PhotoLightbox
          photos={flat}
          index={lightbox}
          sets={sets}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
          onCaption={captionPhoto}
          onMove={movePhoto}
          onDelete={deletePhoto}
          onDownload={downloadOne}
        />
      ) : null}

      {drawer ? (
        <PhotoSetDrawer
          jobId={jobId}
          jobNumber={jobNumber}
          drawingSets={drawingSets}
          set={drawer.set}
          onClose={() => setDrawer(null)}
          onSaved={(saved) => void onSetSaved(saved, drawer.set == null)}
          onDeleted={() => void onSetDeleted()}
        />
      ) : null}
    </div>
  );
}
